import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { documentMarkupApi } from '../utils/documentMarkupApi';
import { auth } from '../../../firebase';
import MarkupExportDialog from './MarkupExportDialog';
import './DocumentMarkupList.css';

function DocumentMarkupList() {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [user, setUser] = useState(null);
    const [exportingDocument, setExportingDocument] = useState(null);

    useEffect(() => {
        // Wait for Firebase auth to be ready before loading documents
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            if (currentUser) {
                setUser(currentUser);
                loadDocuments();
            } else {
                setLoading(false);
                setError('Please sign in to view your documents');
            }
        });

        return () => unsubscribe();
    }, []);

    const loadDocuments = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await documentMarkupApi.listDocuments();
            setDocuments(data.documents || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (documentId, fileName) => {
        if (!window.confirm(`Delete "${fileName}"? This cannot be undone.`)) {
            return;
        }

        try {
            await documentMarkupApi.deleteDocument(documentId);
            setDocuments(docs => docs.filter(d => d.id !== documentId));
        } catch (err) {
            alert('Failed to delete document: ' + err.message);
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="DocumentMarkupList__container">
                <div className="DocumentMarkupList__loading">Loading documents...</div>
            </div>
        );
    }

    return (
        <div className="DocumentMarkupList__container">
            <div className="DocumentMarkupList__header">
                <div className="DocumentMarkupList__nav-buttons">
                    <button 
                        className="DocumentMarkupList__nav-btn"
                        onClick={() => navigate('/')}
                        title="Go to Home"
                    >
                        <i className="fas fa-home"></i> Home
                    </button>
                    <button 
                        className="DocumentMarkupList__nav-btn"
                        onClick={() => navigate(-1)}
                        title="Go Back"
                    >
                        <i className="fas fa-arrow-left"></i> Back
                    </button>
                    <button 
                        className="DocumentMarkupList__nav-btn"
                        onClick={() => navigate('/my-notebook')}
                        title="My Notebook"
                    >
                        <i className="fas fa-book"></i> My Notebook
                    </button>
                </div>
                <h2>Document Markups</h2>
                <button 
                    className="DocumentMarkupList__upload-btn"
                    onClick={() => navigate('/my-notebook/document-markup/upload')}
                >
                    <i className="fas fa-upload"></i> Upload Document
                </button>
            </div>

            {error && (
                <div className="DocumentMarkupList__error">
                    <i className="fas fa-exclamation-circle"></i> {error}
                </div>
            )}

            {documents.length === 0 ? (
                <div className="DocumentMarkupList__empty">
                    <i className="fas fa-file-alt"></i>
                    <p>No documents uploaded yet</p>
                    <p className="DocumentMarkupList__empty-hint">
                        Upload a PDF or image to start marking it up
                    </p>
                </div>
            ) : (
                <div className="DocumentMarkupList__grid">
                    {documents.map(doc => (
                        <div key={doc.id} className="DocumentMarkupList__card">
                            <div className="DocumentMarkupList__card-icon">
                                <i className={`fas fa-file-${doc.file_type === 'pdf' ? 'pdf' : 'image'}`}></i>
                            </div>
                            <div className="DocumentMarkupList__card-content">
                                <h3 className="DocumentMarkupList__card-title">{doc.file_name}</h3>
                                <div className="DocumentMarkupList__card-meta">
                                    <span>{formatFileSize(doc.file_size)}</span>
                                    <span>•</span>
                                    <span>{doc.stroke_count} annotations</span>
                                    <span>•</span>
                                    <span>{formatDate(doc.updated_at)}</span>
                                </div>
                            </div>
                            <div className="DocumentMarkupList__card-actions">
                                <button
                                    onClick={() => navigate(`/my-notebook/document-markup/${doc.id}`)}
                                    className="DocumentMarkupList__btn-edit"
                                    title="Edit markup"
                                >
                                    <i className="fas fa-pen"></i>
                                </button>
                                <button
                                    onClick={() => setExportingDocument(doc)}
                                    className="DocumentMarkupList__btn-export"
                                    title="Export annotated PDF"
                                >
                                    <i className="fas fa-file-pdf"></i>
                                </button>
                                <button
                                    onClick={() => handleDelete(doc.id, doc.file_name)}
                                    className="DocumentMarkupList__btn-delete"
                                    title="Delete document"
                                >
                                    <i className="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="DocumentMarkupList__info">
                <i className="fas fa-info-circle"></i>
                Uploads count toward your storage limit (max 10MB each)
            </div>

            {exportingDocument && (
                <MarkupExportDialog
                    markup={exportingDocument}
                    onClose={() => setExportingDocument(null)}
                />
            )}
        </div>
    );
}

export default DocumentMarkupList;
